import {
  ILogger,
  ILoggerFactory,
  LogLevel,
  LogMessageMetadata,
  LogMessageMetadataCallback,
  LogRouter,
} from './types.js';

export class LoggerImpl implements ILogger {
  private metadata: LogMessageMetadata = {};

  constructor(
    private readonly id: string,
    private readonly factory: ILoggerFactory,
    private readonly router: LogRouter,
    private readonly parent: ILogger | null,
    public readonly level: LogLevel,
    metadata?: LogMessageMetadata,
  ) {
    this.metadata = { ...metadata };
  }

  log(level: LogLevel, message: string, metadataCallback?: LogMessageMetadataCallback): LoggerImpl {
    if (level < this.level) {
      return this;
    }
    const metadata = metadataCallback ? metadataCallback() : undefined;
    this.router.log(this.level, {
      ...this.getMetadata(),
      ...metadata,
      level,
      message,
      ts: Date.now(),
      logger: this.id,
    });
    return this;
  }

  trace(message: string, metadataCallback?: LogMessageMetadataCallback): ILogger {
    return this.log(LogLevel.trace, message, metadataCallback);
  }

  debug(message: string, metadataCallback?: LogMessageMetadataCallback): ILogger {
    return this.log(LogLevel.debug, message, metadataCallback);
  }

  info(message: string, metadataCallback?: LogMessageMetadataCallback): ILogger {
    return this.log(LogLevel.info, message, metadataCallback);
  }

  warn(message: string, metadataCallback?: LogMessageMetadataCallback): ILogger {
    return this.log(LogLevel.warn, message, metadataCallback);
  }

  error(message: string, metadataCallback?: LogMessageMetadataCallback): ILogger {
    return this.log(LogLevel.error, message, metadataCallback);
  }

  registerMetadata(metadata: LogMessageMetadata): ILogger {
    this.metadata = Object.assign(this.metadata, metadata);
    return this;
  }

  getMetadata(): LogMessageMetadata {
    return {
      ...this.parent?.getMetadata(),
      ...this.metadata,
    };
  }

  extend(id: string | Array<string>): ILogger {
    if (Array.isArray(id)) {
      return this.factory.createLogger([this.id, ...id], {}, this);
    }
    return this.factory.createLogger([this.id, id], {}, this);
  }
}
